import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import {
  MembershipMetrics,
  MembershipMetricsBucket,
  MembershipMetricGranularity,
  MembershipBreakdownItem,
} from "../../model/members/MembershipMetrics";

// The API returns dates as YYYY-MM-DD; DateReviver may hydrate them into Date objects on the way
// through the base client, so normalise back to plain YYYY-MM-DD strings (UTC) for the UI.
function toDateString(value: unknown): string {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  return "";
}

function toNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeBucket(raw: Record<string, unknown>): MembershipMetricsBucket {
  return {
    periodStart: toDateString(raw.periodStart),
    newMembers: toNumber(raw.newMembers),
    lostMembers: toNumber(raw.lostMembers),
    netGrowth: toNumber(raw.netGrowth),
    openingTotal: toNumber(raw.openingTotal),
    closingTotal: toNumber(raw.closingTotal),
  };
}

function normalizeBreakdown(raw: unknown): MembershipBreakdownItem[] {
  return Array.isArray(raw)
    ? (raw as Record<string, unknown>[]).map((item) => ({
        name: String(item.name ?? "Unknown"),
        count: toNumber(item.count),
      }))
    : [];
}

function normalizeMetrics(raw: Record<string, unknown>): MembershipMetrics {
  const granularity: MembershipMetricGranularity =
    raw.granularity === "week" ? "week" : "month";
  const buckets = Array.isArray(raw.buckets)
    ? (raw.buckets as Record<string, unknown>[]).map(normalizeBucket)
    : [];

  return {
    from: toDateString(raw.from),
    to: toDateString(raw.to),
    granularity,
    // The API echoes back the window it applied (it clamps out-of-range values), so the screen states
    // what the figures were actually computed with rather than what it asked for. Left null when the
    // response carries no window rather than assuming the default — an API that ignored the parameter
    // computed these figures with no window at all, and saying otherwise would misdescribe them.
    renewalGraceDays: raw.renewalGraceDays == null ? null : toNumber(raw.renewalGraceDays),
    buckets,
    totalNewMembers: toNumber(raw.totalNewMembers),
    totalLostMembers: toNumber(raw.totalLostMembers),
    netGrowth: toNumber(raw.netGrowth),
    leadsInPeriod: toNumber(raw.leadsInPeriod),
    conversionsInPeriod: toNumber(raw.conversionsInPeriod),
    leadToMemberConversionRate: toNumber(raw.leadToMemberConversionRate),
    totalActiveMembers: toNumber(raw.totalActiveMembers),
    howTheyHeard: normalizeBreakdown(raw.howTheyHeard),
    cancellationReasons: normalizeBreakdown(raw.cancellationReasons),
    membershipTypes: normalizeBreakdown(raw.membershipTypes),
  };
}

export class MemberMetricsService extends ClubBookingApiClient {
  async getMembershipMetrics(
    from: string,
    to: string,
    granularity: MembershipMetricGranularity,
    renewalGraceDays: number
  ): Promise<MembershipMetrics> {
    const raw = await this.get<Record<string, unknown>>(
      "/members/metrics",
      { from, to, granularity, renewalGraceDays },
      { showError: true }
    );
    return normalizeMetrics(raw ?? {});
  }
}
